import React, { useMemo } from "react";
import Markdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ImageWithActions } from "./ImageWithActions";

function isRTL(text: string) {
  // Any Hebrew/Arabic character flips the message to RTL. The previous
  // 30%-of-chars threshold misclassified short Hebrew replies as LTR
  // (e.g. "\u05E9\u05DC\u05D5\u05DD!" \u2014 punctuation/whitespace diluted the ratio).
  return /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(text);
}

function createMarkdownComponents(
  track?: (eventName: string, properties: any) => void,
): Components {
  return {
    a: ({ node, children, ...props }) => {
      const href = props.href ?? "";

      const childText = React.Children.toArray(children)
        .map((c) => (typeof c === "string" ? c : ""))
        .join("")
        .trim()
        .toLowerCase();

      const isImageLink =
        !!href &&
        /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(href) &&
        (childText === "image" || childText === "img" || childText === "");

      if (isImageLink) {
        return <ImageWithActions src={href} alt={childText || "inline image"} track={track} />;
      }

      return (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            track?.("cta_button_clicked", {
              entry_page: window.location.pathname,
              link: href,
              chatId: window.quack?.chatSessionId,
              tenant: window.quack?.settings?.tenantId,
              event: "cta_button_clicked",
            });
          }}
        >
          {children}
        </a>
      );
    },

    // For real markdown images: ![alt](url)
    img: ({ node, ...props }) => (
      <ImageWithActions src={props.src ?? ""} alt={props.alt} track={track} />
    ),
  };
}

// Helper to extract iframe URL and its position if present in the template
function extractIframeUrlAndParts(text: string): {
  url: string | null;
  before: string;
  after: string;
} {
  const match = text
    .split("`")
    .join("")
    .match(/([\s\S]*?)iframe:(https?:\/\/[^\s]+)([\s\S]*)/);
  if (match) {
    return {
      url: match[2].split("`").join(""),
      before: match[1] || "",
      after: match[3] || "",
    };
  }
  return { url: null, before: "", after: "" };
}

interface MessageTextProps {
  text: string;
  track?: (eventName: string, properties: any) => void;
}

export function MessageText({ text, track }: MessageTextProps) {
  const isRtl = useMemo(() => isRTL(text), [text]);
  const { url, before, after } = extractIframeUrlAndParts(text);

  const components = useMemo(() => createMarkdownComponents(track), [track]);

  if (url) {
    return (
      <div className="markdown-body" style={{
        direction: isRtl ? "rtl" : "ltr",
        textAlign: isRtl ? "right" : undefined,
      }}>
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
          {before}
        </Markdown>
        <iframe src={url} style={{ height: "350px", border: "1px solid #f2f2f2" }} />
        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
          {after}
        </Markdown>
      </div>
    );
  }

  return (
    <div className="markdown-body" style={{
        direction: isRtl ? "rtl" : "ltr",
        textAlign: isRtl ? "right" : undefined,
      }}>
      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={components}>
        {text}
      </Markdown>
    </div>
  );
}
